import MediaPlayer                          from 'hasplayer.js';
import {Observable, Subject, Subscription}    from 'rxjs';

import IErrorsPayload               from '../../Player/Interfaces/IErrorsPayload';
import Player                       from '../../Player/Player';
import {getUserDevice}              from '../../Shared/Util';
import Media                        from '../../State/Stores/Media/Media';
import Html5Base                    from '../Shared/Abstract/Html5Base';
import SolutionType                 from '../Shared/Constants/SolutionType';
import SyntheticMediaEventType from '../Shared/Constants/SyntheticMediaEventType';
import IMediaEventsStreamPayload    from '../Shared/Interfaces/IMediaEventsStreamPayload';
import ISolutionState               from '../Shared/Interfaces/ISolutionState';
import createMediaEventsStream      from '../Shared/Streams/createMediaEventsStream';
import createVideoElement           from '../Shared/Util/createVideoElement';
import extractMpxTokenFromAuthToken from '../Shared/Util/extractMpxTokenFromAuthToken';

import connectSolutionSubscriptions from './connectSolutionsToSubscriptions';
import createMediaErrorsStream      from './Streams/createMediaErrorStream';

class HasPlayer extends Html5Base {
    public type: SolutionType;
    public version: string;
    public mediaEvents$: Observable<IMediaEventsStreamPayload>;
    public mediaErrors$: Observable<IErrorsPayload>;
    public videoElement: HTMLVideoElement;
    public activeTextTrack: TextTrack;
    protected subscriptions: Subscription[];
    protected syntheticMediaEvents$: Subject<SyntheticMediaEventType>;
    private player: Player;
    private hasPlayer: any;

    constructor(player: Player, type: SolutionType) {
        super();
        const {stores} = player.state;

        this.type = type;
        this.player = player;
        this.version = getUserDevice(navigator.userAgent).browserVersion;

        this.videoElement = createVideoElement(player.refs.solution, this.player.state.stores.volume.value.level);
        this.hasPlayer = HasPlayer.createHasPlayer(this.videoElement);

        this.mediaErrors$ = createMediaErrorsStream(this.hasPlayer, stores.playback.update$);

        this.mediaEvents$ = createMediaEventsStream(stores, this, this.syntheticMediaEvents$);
        this.subscriptions = connectSolutionSubscriptions(stores, this);

        this.setActiveTextTrack = super.notImplemented.bind(this);
    }

    public get state(): ISolutionState {
        const {start, end} = this.seekRange;
        const {currentTime, volume, videoHeight, videoWidth} = this.videoElement;

        const state = {
            minTime: start,
            maxTime: end,
            currentTime,
            resolution: `${videoWidth}x${videoHeight}`,
            volume,
            activeCues: null,
            estimatedBandwidth: -1
        };

        return state;
    }

    public load(media: Media): void {
        const {isEncrypted, src, startTime} = media;

        const loadConfig: any = {};
        loadConfig.url = src;

        if (startTime > 0) loadConfig.starTime = startTime;

        if (isEncrypted) {
            loadConfig.protData = this.configureContentProtection();
        }

        this.hasPlayer.load(loadConfig);
        this.syntheticMediaEvents$.next(SyntheticMediaEventType.READY);
    }

    public unload(): void {
        this.pause();

        this.hasPlayer.reset();
    }

    public async getBitrate(): Promise<number> {
        const bitrateList = this.hasPlayer.getVideoBitrates('video');
        const qualityIndex = this.hasPlayer.getQualityFor('video');
        const activeBitrateInfo = bitrateList.find(bitrateInfo => bitrateInfo.qualityIndex === qualityIndex);

        return activeBitrateInfo ? activeBitrateInfo.bitrate : 0;
    }

    public async destroy(): Promise<void> {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.hasPlayer.reset();
    }

     public setActiveTextTrack(): void {
        super.notImplemented.call(this);
    }

    /*
    ** Shaka's equivalent to seekRange - Not documented
    ** http://orange-opensource.github.io/hasplayer.js/1.5.0/doc/MediaPlayer.html
    */
    private get seekRange(): any {
        return this.hasPlayer.getDVRWindowRange() || {
            start: 0,
            end: this.videoElement.duration
        };
    }

    private configureContentProtection(): void {
        const {config} = this.player.state.stores;
        const {licenseUrl, authToken} = config.value.drm;

        return ({
            'com.microsoft.playready': {
                laURL: `${licenseUrl}&auth=${extractMpxTokenFromAuthToken(authToken)}`
            }
        } as any);
    }

    private static createHasPlayer(videoElement: HTMLVideoElement): any {
        const hasPlayer = new MediaPlayer();

        hasPlayer.init(videoElement);

        return hasPlayer;
    }
}

export default HasPlayer;
